"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils/cn";
import { stripMarkdown } from "@/lib/utils/format";
import {
  highlightGlossaryTerms,
  type GlossaryEntry,
} from "@/lib/utils/highlight-glossary";
import { ChevronDown, Lock } from "lucide-react";
import type { LucideIcon } from "lucide-react";

type FundSectionProps = {
  title: string;
  icon: LucideIcon;
  financialContent: string;
  simpleContent: string;
  activeTab: "financial" | "simple";
  defaultExpanded?: boolean;
  isLocked?: boolean;
  glossaryTerms?: GlossaryEntry[];
};

export function FundSection({
  title,
  icon: Icon,
  financialContent,
  simpleContent,
  activeTab,
  defaultExpanded = false,
  isLocked = false,
  glossaryTerms = [],
}: FundSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Tab switch fade: 200ms opacity transition on content. Re-mount key swap
  // is unnecessary; we just flip opacity off → on when activeTab changes.
  const [contentVisible, setContentVisible] = useState(true);
  useEffect(() => {
    setContentVisible(false);
    const t = setTimeout(() => setContentVisible(true), 60);
    return () => clearTimeout(t);
  }, [activeTab]);

  // Pick content for the active tab; fall back to the other side if the
  // selected one is empty. Track whether we fell back so we can show a
  // muted note to the user.
  const primary = activeTab === "financial" ? financialContent : simpleContent;
  const fallback = activeTab === "financial" ? simpleContent : financialContent;
  const usingFallback = !primary?.trim() && !!fallback?.trim();
  const rawContent = primary?.trim() ? primary : fallback ?? "";
  const paragraphs = stripMarkdown(rawContent).split("\n\n").filter(Boolean);

  return (
    <div className="bg-surface border border-surface-light/30 rounded-xl overflow-hidden">
      {/* Header — always visible, toggles expand/collapse */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer border-none bg-transparent hover:bg-surface-light/5 transition"
      >
        <Icon className="w-5 h-5 text-blue shrink-0" />
        <span className="text-foreground font-serif text-sm flex-1">
          {title}
        </span>
        {isLocked && (
          <span className="flex items-center gap-1 bg-gold/20 text-gold text-[10px] font-bold px-2 py-0.5 rounded-full mr-2">
            <Lock className="w-3 h-3" />
            PRO
          </span>
        )}
        <ChevronDown
          className={cn(
            "w-4 h-4 text-muted-foreground transition-transform duration-200",
            expanded && "rotate-180"
          )}
        />
      </button>

      {/* Expandable content area */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-in-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-5 relative">
            {isLocked ? (
              <>
                {/* Blurred preview */}
                <div className="blur-sm select-none pointer-events-none">
                  {paragraphs.slice(0, 2).map((p, i) => (
                    <p
                      key={i}
                      className="text-muted-foreground text-sm leading-relaxed mb-3 last:mb-0"
                    >
                      {p}
                    </p>
                  ))}
                </div>
                {/* Lock overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-surface/60 backdrop-blur-[2px] rounded-b-xl">
                  <div className="flex flex-col items-center gap-2">
                    <Lock className="w-6 h-6 text-gold" />
                    <span className="text-foreground text-sm font-semibold">
                      Upgrade to PRO to unlock
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="transition-opacity duration-200"
                style={{ opacity: contentVisible ? 1 : 0 }}
              >
                {usingFallback && (
                  <p className="text-dim text-[11px] italic mb-3">
                    {activeTab === "simple"
                      ? "Simple English translation not available. Showing financial language."
                      : "Financial language version not available. Showing simple English."}
                  </p>
                )}
                {paragraphs.length > 0 ? (
                  paragraphs.map((p, i) => (
                    <p
                      key={i}
                      className="text-muted-foreground text-sm leading-relaxed mb-3 last:mb-0"
                    >
                      {glossaryTerms.length > 0
                        ? highlightGlossaryTerms(p, glossaryTerms)
                        : p}
                    </p>
                  ))
                ) : (
                  <p className="text-dim text-xs italic">
                    Content not available for this section.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
